import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatCurrency, formatDate } from '../utils/api';
import { generatePDF } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUSES = ['','paid','unpaid','overdue','draft'];
const badge = (s) => <span className={`badge badge-${s}`}>{s}</span>;

export default function Invoices() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetch = useCallback(() => {
    setLoad(true);
    api.get('/invoices', { params:{ status:filter, search } })
      .then(r=>{ setRows(r.data.data||[]); setLoad(false); }).catch(()=>setLoad(false));
  }, [filter, search]);

  useEffect(()=>{ const t=setTimeout(fetch,300); return ()=>clearTimeout(t); },[fetch]);

  const del = async (id) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    await api.delete(`/invoices/${id}`); toast.success('Invoice deleted'); fetch();
  };

  const togglePaid = async (inv) => {
    const next = inv.status === 'paid' ? 'unpaid' : 'paid';
    await api.put(`/invoices/${inv.id}/status`, { status: next });
    toast.success(`Marked as ${next}`); fetch();
  };

  const dlPdf = async (id) => {
    const res = await api.get(`/invoices/${id}`);
    await generatePDF(res.data.data, 'invoice', user);
  };

  const whatsapp = async (inv) => {
    const res = await api.get(`/invoices/${inv.id}`);
    const d = res.data.data;
    const txt = `Hi ${d.client_name},\n\nYour invoice *${d.invoice_number}* for *${formatCurrency(d.total)}* is *${d.status.toUpperCase()}*.\nDue: ${formatDate(d.due_date)}\n\nThank you,\n${user?.company_name||''}`;
    window.open(`https://wa.me/${inv.client_phone?.replace(/\D/g,'')||''}?text=${encodeURIComponent(txt)}`, '_blank');
  };

  return (
    <div>
      <div className="page-header">
        <div><h2 className="page-title">Invoices</h2><p className="page-subtitle">{rows.length} record{rows.length!==1?'s':''}</p></div>
        <div className="page-actions">
          <div className="search-box"><span>🔍</span><input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} /></div>
          {isAdmin && <button className="btn btn-primary" onClick={()=>nav('/invoices/new')}>+ New Invoice</button>}
        </div>
      </div>

      <div className="tabs">
        {STATUSES.map(s=>(
          <div key={s} className={`tab ${filter===s?'active':''}`} onClick={()=>setFilter(s)}>
            {s?s.charAt(0).toUpperCase()+s.slice(1):'All'}
          </div>
        ))}
      </div>

      {!isAdmin && (
        <div className="alert alert-warn" style={{ marginBottom:14 }}>
          🔒 Staff view — you can toggle paid/unpaid status but cannot edit or delete invoices.
        </div>
      )}

      <div className="card" style={{ padding:0 }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--text3)' }}>Loading…</div>
        : rows.length===0 ? (
          <div className="empty-state"><div className="empty-icon">🧾</div><h3>No invoices</h3>
            {isAdmin && <><p>Create your first invoice</p><button className="btn btn-primary" onClick={()=>nav('/invoices/new')}>+ New Invoice</button></>}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Number</th><th>Client</th><th>Issue Date</th><th>Due Date</th><th>Total</th><th>Payment</th><th>Actions</th></tr></thead>
              <tbody>
                {rows.map(inv=>(
                  <tr key={inv.id}>
                    <td style={{ fontWeight:700,color:'var(--accent2)' }}>{inv.invoice_number}</td>
                    <td>
                      <div>{inv.client_name}</div>
                      {inv.client_phone && <div style={{ fontSize:11,color:'var(--text3)' }}>{inv.client_phone}</div>}
                    </td>
                    <td>{formatDate(inv.issue_date)}</td>
                    <td style={{ color:inv.status==='overdue'?'var(--err)':'inherit',fontWeight:inv.status==='overdue'?600:400 }}>{formatDate(inv.due_date)}</td>
                    <td style={{ fontWeight:600 }}>{formatCurrency(inv.total)}</td>
                    <td>
                      <button className={`btn btn-sm ${inv.status==='paid'?'btn-success':'btn-warn'}`}
                        onClick={() => togglePaid(inv)}
                        title={inv.status==='paid'?'Mark Unpaid':'Mark Paid'}>
                        {inv.status==='paid' ? '✅ Paid' : '⏳ Unpaid'}
                      </button>
                    </td>
                    <td>
                      <div style={{ display:'flex',gap:4 }}>
                        {isAdmin && <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>nav(`/invoices/${inv.id}/edit`)} title="Edit">✏️</button>}
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>dlPdf(inv.id)} title="Download PDF">📄</button>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>whatsapp(inv)} title="WhatsApp">💬</button>
                        {isAdmin && <button className="btn btn-sm btn-danger btn-icon" onClick={()=>del(inv.id)} title="Delete">🗑️</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
